import type { ReactNode } from 'react';
import {
  Document,
  Page as PdfPage,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import {
  deepCoerceFrameworkText,
  frameworkText,
  normalizeMission,
  hasRichText,
} from '../../../lib/syllabus-text';
import type { SyllabusPdfLabels } from '../../../lib/syllabus-pdf-labels';
import {
  buildAssignmentDistribution,
  buildWorkloadRows,
  buildCourseCalendar,
  type AssignmentTemplateType,
  type ReadingPlanRow,
} from '@dosfilos/domain';
import { showKeyTextsColumn } from '../../../lib/syllabus-pdf-key-texts';

// EB Garamond (Open Font License) hosted locally under apps/web/public/fonts.
// Self-hosted avoids 404s and CORS issues with Google Fonts CDN.
Font.register({
  family: 'EB Garamond',
  fonts: [
    { src: '/fonts/EBGaramond-Regular.ttf' },
    { src: '/fonts/EBGaramond-Bold.ttf', fontWeight: 700 },
    { src: '/fonts/EBGaramond-Italic.ttf', fontStyle: 'italic' },
    { src: '/fonts/EBGaramond-BoldItalic.ttf', fontWeight: 700, fontStyle: 'italic' },
  ],
});

// Default copy, month names, weekday names and every date sentence now live in
// `buildSyllabusPdfLabels` — see the note there on why this document receives
// its strings as data instead of calling `t()` itself.

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function splitTitle(title: string): { main: string; sub?: string } {
  const m = title.match(/^(.*?)\s*[—–-]\s+(.+)$/);
  if (!m) return { main: title };
  return { main: m[1]!.trim(), sub: m[2]!.trim() };
}

function clockToMin(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  return parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10);
}

function minToClock(n: number): string {
  const h = Math.floor(n / 60) % 24;
  const mm = n % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

interface TimelineRow {
  kind: 'section' | 'break';
  label: string;
  range: string;
  durationLabel?: string;
}

/** Walk the explicit segment sequence and produce rows with clock ranges. */
function buildDayTimeline(
  startTime: string,
  segments: { kind: 'section' | 'break'; durationMinutes: number; label?: string }[],
  labels: SyllabusPdfLabels,
): TimelineRow[] {
  const start = clockToMin(startTime);
  if (start === null) return [];
  const rows: TimelineRow[] = [];
  let cursor = start;
  let secIdx = 1;
  let brkIdx = 1;
  for (const s of segments) {
    const segStart = cursor;
    const segEnd = cursor + (s.durationMinutes || 0);
    if (s.kind === 'section') {
      rows.push({
        kind: 'section',
        label: s.label?.trim() ? s.label : labels.blockNumbered(secIdx),
        range: `${minToClock(segStart)} – ${minToClock(segEnd)}`,
      });
      secIdx += 1;
    } else {
      rows.push({
        kind: 'break',
        label: s.label?.trim() ? labels.breakLabeled(s.label) : labels.breakNumbered(brkIdx),
        range: `${minToClock(segStart)} – ${minToClock(segEnd)}`,
        durationLabel: labels.minutes(s.durationMinutes),
      });
      brkIdx += 1;
    }
    cursor = segEnd;
  }
  return rows;
}

// Render-time TipTap-ish JSON to plain text for the PDF. We intentionally keep
// rendering minimal (paragraphs + headings + bullet lists) because react-pdf
// doesn't support arbitrary HTML/JSON trees out of the box. Power users still
// edit rich pages in the LMS; the PDF is the canonical syllabus snapshot.

interface DocNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: DocNode[];
  text?: string;
  marks?: { type: string }[];
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56,
    paddingLeft: 60,
    paddingRight: 60,
    fontSize: 11,
    fontFamily: 'EB Garamond',
    color: '#1f2937',
  },
  coverWrap: {
    height: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 60,
    paddingHorizontal: 40,
  },
  coverTitle: {
    fontSize: 30,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 6,
    color: '#0f172a',
    letterSpacing: 0.3,
  },
  coverTopic: {
    fontSize: 18,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 18,
    color: '#1f2937',
  },
  coverTagline: {
    fontSize: 13,
    textAlign: 'center',
    fontStyle: 'italic',
    color: '#7c5f10',
    marginBottom: 30,
  },
  coverDates: {
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 2,
    color: '#1f2937',
  },
  coverDateRange: {
    fontSize: 10,
    textAlign: 'center',
    color: '#475569',
    marginBottom: 28,
  },
  coverInstructor: {
    fontSize: 14,
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 40,
    color: '#0f172a',
  },
  coverFooter: {
    fontSize: 11,
    textAlign: 'center',
    color: '#475569',
    marginBottom: 1,
  },
  coverFooterAlt: {
    fontSize: 10,
    textAlign: 'center',
    color: '#64748b',
  },
  h1: {
    fontSize: 18,
    fontWeight: 700,
    color: '#7c2d12',
    marginTop: 12,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#fcd34d',
    paddingBottom: 4,
  },
  h2: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1f2937',
    marginTop: 10,
    marginBottom: 6,
  },
  h3: {
    fontSize: 12,
    fontWeight: 700,
    color: '#1f2937',
    marginTop: 8,
    marginBottom: 4,
  },
  paragraph: {
    fontSize: 11,
    marginBottom: 5,
    lineHeight: 1.45,
  },
  bold: {
    fontWeight: 700,
  },
  italic: {
    fontStyle: 'italic',
  },
  bullet: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  bulletDot: {
    width: 12,
    fontSize: 11,
  },
  bulletText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 1.45,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    fontWeight: 700,
    fontSize: 10,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
  },
  tableRow: {
    flexDirection: 'row',
    fontSize: 10,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  cell: { flex: 1 },
  cellTight: { width: 64 },
  cellWide: { flex: 2 },
  // ── Rubric grid ───────────────────────────────────────────────────────
  // A real grid layout: each criterion = one row, each performance level =
  // one column. Using View cells (not Text) so multi-line content wraps
  // within its column instead of overflowing into siblings.
  rubricHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
  },
  rubricRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  rubricCellCriterion: {
    flex: 2,
    padding: 4,
    borderRightWidth: 0.5,
    borderRightColor: '#e5e7eb',
  },
  rubricCellLevel: {
    flex: 2,
    padding: 4,
    borderRightWidth: 0.5,
    borderRightColor: '#e5e7eb',
  },
  rubricCellWeight: {
    width: 40,
    padding: 4,
    alignItems: 'center',
  },
  /** Objective column of the distribution matrix. `flex` rather than a fixed
   *  width so a course with many objectives divides the row instead of pushing
   *  past the page and collapsing the title column; `textAlign` rather than
   *  `alignItems`, which does nothing on a <Text>. */
  distCell: {
    flex: 1,
    minWidth: 22,
    padding: 4,
    textAlign: 'center',
  },
  rubricHeaderText: {
    fontSize: 9,
    fontWeight: 700,
  },
  rubricCriterionLabel: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 2,
  },
  rubricCriterionDesc: {
    fontSize: 7.5,
    color: '#475569',
    lineHeight: 1.3,
  },
  rubricCellText: {
    fontSize: 7.5,
    lineHeight: 1.35,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 60,
    right: 60,
    fontSize: 9,
    textAlign: 'center',
    color: '#94a3b8',
    borderTopWidth: 0.5,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 12,
    right: 60,
    fontSize: 9,
    color: '#94a3b8',
  },
  anchorVerseBlock: {
    backgroundColor: '#fef3c7',
    borderLeftWidth: 3,
    borderLeftColor: '#f59e0b',
    padding: 8,
    marginVertical: 8,
  },
  coverLogo: {
    width: 180,
    height: 180,
    objectFit: 'contain',
    marginBottom: 32,
  },
  missionBlock: {
    marginBottom: 12,
  },
  missionScripture: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#475569',
    marginBottom: 4,
  },
});

/** Los estilos de título (h1, h2). Exportados para el test de estructura (S4):
 *  todo título va dentro de un bloque `wrap={false}` junto a su primer contenido. */
export const PDF_HEADING_STYLES: readonly object[] = [styles.h1, styles.h2];

// Render inline content honoring bold/italic marks. Returns react-pdf <Text>
// nodes so emphasis survives into the PDF (Bold/Italic font variants are
// registered above). Walks text nodes and nested inline content.
function renderInline(node: DocNode, keyPrefix: string): ReactNode[] {
  if (node.type === 'text') {
    const marks = node.marks ?? [];
    const markStyles = [
      ...(marks.some((m) => m.type === 'bold') ? [styles.bold] : []),
      ...(marks.some((m) => m.type === 'italic') ? [styles.italic] : []),
    ];
    return [
      markStyles.length > 0 ? (
        <Text key={keyPrefix} style={markStyles}>
          {node.text ?? ''}
        </Text>
      ) : (
        (node.text ?? '')
      ),
    ];
  }
  if (node.content) {
    return node.content.flatMap((child, i) => renderInline(child, `${keyPrefix}-${i}`));
  }
  return [];
}

/** Los bloques de un texto enriquecido, uno por elemento: así el primero puede
 *  viajar pegado al título de su sección (S4) y el resto paginar solo. */
function docBlocks(doc: Record<string, unknown> | undefined): ReactNode[] {
  if (!doc) return [];
  const root = doc as unknown as DocNode;
  const blocks = root.content ?? [];
  return blocks
    .map((b, i) => {
      if (b.type === 'heading') {
        const level = (b.attrs?.['level'] as number) ?? 2;
        const style = level === 1 ? styles.h1 : level === 2 ? styles.h2 : styles.h3;
        return (
          <Text key={i} style={style}>
            {renderInline(b, `h-${i}`)}
          </Text>
        );
      }
      if (b.type === 'paragraph') {
        return (
          <Text key={i} style={styles.paragraph}>
            {renderInline(b, `p-${i}`)}
          </Text>
        );
      }
      if (b.type === 'bulletList' || b.type === 'orderedList') {
        const items = b.content ?? [];
        return (
          <View key={i}>
            {items.map((li, j) => (
              <View key={j} style={styles.bullet} wrap={false}>
                <Text style={styles.bulletDot}>{b.type === 'bulletList' ? '•' : `${j + 1}.`}</Text>
                <Text style={styles.bulletText}>{renderInline(li, `li-${i}-${j}`)}</Text>
              </View>
            ))}
          </View>
        );
      }
      return null;
    })
    .reduce<{ out: ReactNode[]; skip: boolean }>(
      (acc, node, i, all) => {
        if (acc.skip || node === null) return { ...acc, skip: false };
        // Un encabezado del texto viaja pegado al bloque que lo sigue.
        const next = all[i + 1];
        if (blocks[i]?.type === 'heading' && next) {
          acc.out.push(
            <View key={`hg-${i}`} wrap={false}>
              {node}
              {next}
            </View>,
          );
          return { out: acc.out, skip: true };
        }
        acc.out.push(node);
        return acc;
      },
      { out: [], skip: false },
    ).out;
}

function RenderDoc({ doc }: { doc: Record<string, unknown> | undefined }) {
  return <>{docBlocks(doc)}</>;
}

/**
 * Un encabezado NUNCA puede quedar solo al pie de una página.
 *
 * `minPresenceAhead` no alcanza: se evalúa contra el flujo de la página y el
 * encabezado vive dentro del `<View>` de su sección, así que el motor lo deja
 * pasar. Lo que sí es determinista es pegar el encabezado a su primer hijo —y,
 * en una tabla, a su fila de encabezado— dentro de un bloque que no se parte:
 * ese bloque es chico por construcción, nunca más alto que una página, que es
 * la condición bajo la cual `wrap={false}` se comporta.
 *
 * El resto de las filas queda afuera y pagina libremente.
 */
function GluedSection({
  heading,
  header,
  rows,
  tail,
}: {
  heading: ReactNode;
  /** Fila de encabezado de tabla, si la sección es una tabla. */
  header?: ReactNode;
  rows: ReactNode[];
  /** Lo que va pegado a la ÚLTIMA fila (el ancla bíblica al final del documento),
   *  para que no termine sola en una página casi vacía. */
  tail?: ReactNode;
}): JSX.Element | null {
  // S4: UNA SECCIÓN SIN CONTENIDO NO SE IMPRIME, ni su título.
  if (rows.length === 0 && !header) return null;
  const [first, ...rest] = rows;
  const last = rest.length > 0 && tail ? rest.pop() : undefined;
  return (
    <>
      <View wrap={false}>
        {heading}
        {header ?? null}
        {first ?? null}
        {rest.length === 0 && !last ? (tail ?? null) : null}
      </View>
      {rest}
      {last ? (
        <View wrap={false}>
          {last}
          {tail}
        </View>
      ) : null}
    </>
  );
}

// ─── Syllabus PDF document ────────────────────────────────────────────────

export interface SyllabusPdfData {
  template: {
    title: string;
    code?: string;
    description: Record<string, unknown>;
    objectives: {
      program: { id: string; code?: string; text: string }[];
      department: { id: string; code?: string; text: string }[];
      course: { id: string; code?: string; text: string }[];
    };
    philosophy: { id: string; title: string; description: string }[];
    grading: {
      /** La evaluación es la unidad: el mismo ítem lleva el peso, la descripción
       *  que se imprime en "Detalles de la tarea" y las aristas a los OAC de las
       *  que sale la matriz de distribución. */
      components: {
        id: string;
        name: string;
        weight: number;
        description?: Record<string, unknown>;
        objectiveIds?: string[];
        hours?: number;
      }[];
      passingGrade: number;
      expectedHours: { id: string; label: string; hours: number }[];
      note?: Record<string, unknown>;
    };
    readings: {
      required: { textbookId: string; notes?: string }[];
      complementary: {
        intro?: string;
        items: { textbookId: string; notes?: string }[];
      };
      plan?: {
        /** The domain row, not a local restatement — the previous copy omitted
         *  `weekOffset`, the one field this document's calendar joins on, and it
         *  compiled because the field is optional. */
        rows: ReadingPlanRow[];
        introNote?: string;
        description?: string;
        closingNote?: string;
      };
    };
    sessionFormat: {
      weekdayTemplates: {
        id: string;
        weekday: string;
        startTime: string;
        endTime: string;
        segments: {
          id: string;
          kind: 'section' | 'break';
          durationMinutes: number;
          label?: string;
        }[];
      }[];
      introNote?: string;
    };
    modules: {
      id: string;
      name: string;
      axisTopic: string;
    }[];
    assignments: {
      id: string;
      /** Domain union, not `string`: the calendar builder narrows on it, and a
       *  local restatement here would drift from the enum it mirrors. */
      type: AssignmentTemplateType;
      name: string;
      weekOffset: number;
      weight: number;
      pointsPossible: number;
    }[];
    latePolicy?: {
      firstWindowHours: number;
      firstWindowPenalty: number;
      secondWindowHours: number;
      secondWindowPenalty: number;
      dailyPenaltyAfter: number;
    };
    policiesNote?: string;
    assignmentsNote?: Record<string, unknown>;
    /** false: the calendar lists only deliveries and the reading plan prints as
     *  its own table (a section can ask for this — `calendar.showReadings`). */
    calendarShowsReadings?: boolean;
    anchorVerse?: { reference: string; text: string; translation: string };
    biblicalVersion?: string;
  };
  syllabus: {
    instructor: { name: string; credentials?: string };
    termRange: { startDate: string; endDate: string };
    modality: string;
  };
  textbookMap: Record<
    string,
    { title: string; authors: string[]; year?: number; publisher?: string }
  >;
  // Per-module session timetable (resolved by caller)
  moduleSchedule: {
    moduleId: string;
    sessions: {
      date: string;
      startTime: string;
      endTime: string;
      topic: string;
      scriptureRefs?: string;
    }[];
  }[];
  // Deadlines table
  deadlines: { week: number; date: string; entry: string }[];
  tenantName: string;
  /** Higher-resolution logo (or monochrome variant) used on cover. */
  documentLogoUrl?: string;
  /** Institutional mission rendered in Marco institucional section. */
  mission?: { text: string; scripture?: string };
  /** Program purpose (e.g. Maestría en Divinidad) rendered in Marco institucional. */
  programName?: string;
  programPurpose?: string;
  /**
   * Rubrics referenced by this template's assignments (resolved by caller
   * against Program.rubrics with fallback to Tenant.config.rubrics).
   * Renderer outputs one table per rubric, after the assignments section.
   */
  referencedRubrics?: {
    id: string;
    title: string;
    criteria: {
      id: string;
      label: string;
      description: string;
      weight: number;
      levels?: { id: string; name: string; description: string }[];
    }[];
  }[];
  /**
   * Composed academic identity (Phase C+). When present, the renderer prefers
   * these fields over the legacy `template.objectives.*`. The composer fills
   * them from Department/Program/Course; missing layers fall back to the
   * template's own values so the renderer is always safe to call.
   */
  composed?: {
    departmentName?: string;
    departmentPurpose?: string;
    departmentObjectives: { id: string; code?: string; text: string }[];
    programName?: string;
    programPurpose?: string;
    programObjectives: { id: string; code?: string; text: string }[];
    courseObjectives: { id: string; code?: string; text: string }[];
    description?: Record<string, unknown>;
    anchorVerse?: { reference: string; text: string; translation: string };
    biblicalVersion?: string;
    philosophy: { id: string; title: string; description: string }[];
  };
}

export function SyllabusPdfDocument({
  data: rawData,
  labels,
}: {
  data: SyllabusPdfData;
  labels: SyllabusPdfLabels;
}): JSX.Element {
  // Defensive: any institutional field that drifted into the mission shape
  // `{ text, scripture }` (a program/department purpose, a note) crashes PDF
  // generation with React error #31 when rendered raw. Coerce every such object
  // to a string up front so no raw object can reach react-pdf — regardless of
  // which field drifted. `mission` is re-attached, but NORMALIZED (not raw):
  // drifted data nests it as `{ text: { text, scripture } }`, so the raw object
  // would still render `mission.text` (an object) and crash — normalizeMission
  // flattens it to `{ text: string, scripture? }`.
  const safeMission = normalizeMission(rawData.mission);
  const data: SyllabusPdfData = {
    ...deepCoerceFrameworkText(rawData),
    ...(safeMission ? { mission: safeMission } : {}),
  };
  const {
    template,
    syllabus,
    textbookMap,
    moduleSchedule,
    deadlines,
    tenantName,
    documentLogoUrl,
    mission,
    programName,
    programPurpose,
    composed,
  } = data;
  const headerLabel = `${template.title}${template.code ? ` — ${template.code}` : ''} · ${tenantName}`;

  // Phase C+: prefer composed values from the Department/Program/Course
  // chain over the template's legacy fields. The composer fills these from
  // upstream entities; the helpers below pick the freshest non-empty source.
  const description = composed?.description ?? template.description;
  const programOAPs =
    composed && composed.programObjectives.length > 0
      ? composed.programObjectives
      : template.objectives.program;
  const departmentOADs =
    composed && composed.departmentObjectives.length > 0
      ? composed.departmentObjectives
      : template.objectives.department;
  const courseOACs =
    composed && composed.courseObjectives.length > 0
      ? composed.courseObjectives
      : template.objectives.course;
  const philosophy =
    composed && composed.philosophy.length > 0 ? composed.philosophy : template.philosophy;
  // Built by the domain so this document and the on-screen syllabus cannot derive
  // the same matrix differently — the divergence #410 removed.
  // Las filas son las EVALUACIONES: las mismas que la tabla de porcentajes de
  // arriba, en el mismo orden. Un lector puede recorrer "Ensayo de
  // investigación" desde su peso hasta su descripción, su fila en esta matriz y
  // su entrega en el calendario sin tener que adivinar que son lo mismo.
  const distribution = buildAssignmentDistribution(template.grading.components, courseOACs);

  // Una sola tabla de horas, armada por el dominio: las evaluaciones que
  // declaran horas y después la dedicación que no se califica. Que la sume este
  // documento por su cuenta es cómo terminaría dando un total distinto al de la
  // pantalla del alumno.
  const workload = buildWorkloadRows(template.grading.components, template.grading.expectedHours);

  // Merge the reading plan with the deliverables into ONE week-indexed calendar,
  // grouped by the domain so this document and the screen cannot group it
  // differently.
  //
  // The deliverables come from `deadlines`, which the use case builds from the
  // section's LIVE Assignment records — not from `template.assignments`. The
  // template only knows what the section was burned from: an assignment added to
  // the section afterwards is absent from it, one renamed there still carries the
  // old name, and one deleted there is still listed. The template list is the
  // fallback for the editor preview, which has no section and therefore no
  // deadlines at all.
  //
  // Dates travel WITH each deliverable instead of in a column of their own: two
  // submissions in the same week can fall on different days, and a single date
  // per row would have to pick one and misstate the other.
  const calendarAssignments =
    deadlines.length > 0
      ? deadlines.map((d, i) => ({
          id: `dl-${i}`,
          name: d.date && d.date !== '—' ? `${d.entry} — ${d.date}` : d.entry,
          type: 'other' as AssignmentTemplateType,
          weekOffset: d.week - 1,
        }))
      : (template.assignments ?? []);

  const calendarShowsReadings = template.calendarShowsReadings !== false;
  const calendarWeeks = buildCourseCalendar(
    calendarShowsReadings ? template.readings.plan?.rows : undefined,
    calendarAssignments,
  ).filter(
    // Readings with no week stay out — they print in full in the reading-load
    // table above, and listing them here would make an unmigrated template look
    // like nothing had a date. An ASSIGNMENT with no week is different: it has no
    // other listing, so its bucket is kept.
    (w) => w.weekOffset !== null || w.assignments.length > 0,
  );

  const calendarLines = (w: (typeof calendarWeeks)[number]): string =>
    [
      ...w.readings.map((r) => `• ${r.label}${r.pages ? ` ${labels.pagesShort(r.pages)}` : ''}`),
      ...w.assignments.map((a) => a.name),
    ].join('\n');
  const anchorVerse = composed?.anchorVerse ?? template.anchorVerse;
  // S4: el ancla bíblica cierra el documento y nunca termina sola en una página
  // casi vacía: viaja pegada al último ítem de la bibliografía complementaria
  // o, si no hay, al último bloque de Políticas.
  const anchorBlock = anchorVerse ? (
    <View style={styles.anchorVerseBlock}>
      <Text style={styles.paragraph}>
        <Text style={{ fontWeight: 700 }}>{labels.anchorVersePrefix}</Text>"{anchorVerse.text}" —{' '}
        {anchorVerse.reference} ({anchorVerse.translation})
      </Text>
    </View>
  ) : null;
  const hasComplementary = template.readings.complementary.items.length > 0;
  const hasPolicies = !!(template.policiesNote || template.latePolicy);
  const anchorWithPolicies = !hasComplementary && hasPolicies ? anchorBlock : null;
  const biblicalVersion = composed?.biblicalVersion ?? template.biblicalVersion;
  const departmentName = composed?.departmentName;
  const departmentPurpose = composed?.departmentPurpose;
  // Phase E+: prefer composer's program name/purpose. Caller's
  // `programName`/`programPurpose` props are the legacy editor path
  // (lookup via courseId → programId), which silently misses when the
  // template has programId set directly without a courseId link.
  const effectiveProgramName = composed?.programName ?? programName;
  const effectiveProgramPurpose = composed?.programPurpose ?? programPurpose;

  // Suppress lint warnings for fields we now defer to the composed path.
  void biblicalVersion;

  const formatTb = (id: string, opts?: { pages?: number; notes?: string }) => {
    const tb = textbookMap[id];
    if (!tb) return labels.textbookFallback(id);
    const authors = tb.authors.join(' y ');
    const year = tb.year ? `, ${tb.year}` : '';
    const publisher = tb.publisher ? `. ${tb.publisher}` : '';
    const notes = opts?.notes ? ` — ${opts.notes}` : '';
    const pages = opts?.pages !== undefined ? labels.pagesSuffix(opts.pages) : '';
    return `${authors}${year}. ${tb.title}${publisher}${notes}${pages}.`;
  };

  return (
    <Document>
      {/* Cover */}
      <PdfPage size="A4" style={styles.page}>
        <View style={styles.coverWrap}>
          {documentLogoUrl && <Image src={documentLogoUrl} style={styles.coverLogo} />}

          {(() => {
            const { main, sub } = splitTitle(template.title);
            const mainLine = template.code ? `${main} – ${template.code}` : main;
            return (
              <>
                <Text style={styles.coverTitle}>{mainLine}</Text>
                {sub && <Text style={styles.coverTopic}>{sub}</Text>}
              </>
            );
          })()}

          <Text style={styles.coverTagline}>{labels.coverTagline}</Text>

          <Text style={styles.coverDates}>
            {labels.termHeading(syllabus.termRange.startDate, syllabus.termRange.endDate)}
          </Text>
          {(() => {
            const range = labels.termRange(
              syllabus.termRange.startDate,
              syllabus.termRange.endDate,
            );
            return range ? <Text style={styles.coverDateRange}>{range}</Text> : null;
          })()}

          <Text style={styles.coverInstructor}>
            {syllabus.instructor.name}
            {syllabus.instructor.credentials ? `, ${syllabus.instructor.credentials}` : ''}
          </Text>

          <Text style={styles.coverFooter}>{tenantName}</Text>
          {programName && <Text style={styles.coverFooterAlt}>{programName}</Text>}
        </View>
      </PdfPage>

      {/* Continuous body — react-pdf auto-paginates; wrap={false} keeps
          headings glued to their first paragraph. Sections grouped by
          academic layer (Department → Program → Course) so each parent
          appears with its objectives immediately below instead of having
          purposes in one block and objectives in another. */}
      <PdfPage size="A4" style={styles.page}>
        {/* Misión institucional (Tenant level) */}
        {mission?.text && (
          <View wrap={false}>
            <Text style={styles.h1}>{labels.framework}</Text>
            <View style={styles.missionBlock}>
              <Text style={styles.h2}>
                {labels.mission} {mission.scripture ? `(${mission.scripture})` : ''}
              </Text>
              <Text style={styles.paragraph}>{mission.text}</Text>
            </View>
          </View>
        )}

        {/* Departamento: purpose + OADs together */}
        {(frameworkText(departmentPurpose) || departmentOADs.length > 0) && (
          <View>
            {/* S4: el título del departamento va pegado a su propósito o, si no
                hay, a los objetivos. */}
            {frameworkText(departmentPurpose) ? (
              <View wrap={false}>
                <Text style={styles.h1}>
                  {labels.department}
                  {departmentName ? ` — ${departmentName}` : ''}
                </Text>
                <Text style={styles.paragraph}>{frameworkText(departmentPurpose)}</Text>
              </View>
            ) : null}
            {departmentOADs.length > 0 && (
              <GluedSection
                heading={
                  <>
                    {!frameworkText(departmentPurpose) && (
                      <Text style={styles.h1}>
                        {labels.department}
                        {departmentName ? ` — ${departmentName}` : ''}
                      </Text>
                    )}
                    <Text style={styles.h2}>{labels.departmentObjectives}</Text>
                  </>
                }
                rows={departmentOADs.map((o) => (
                  <View key={o.id} style={styles.bullet} wrap={false}>
                    <Text style={styles.bulletDot}>•</Text>
                    <Text style={styles.bulletText}>
                      {o.code ? `${o.code}. ` : ''}
                      {o.text}
                    </Text>
                  </View>
                ))}
              />
            )}
          </View>
        )}

        {/* Programa: purpose + OAPs together */}
        {(frameworkText(effectiveProgramPurpose) || programOAPs.length > 0) && (
          <View>
            {frameworkText(effectiveProgramPurpose) ? (
              <View wrap={false}>
                <Text style={styles.h1}>
                  {labels.program}
                  {effectiveProgramName ? ` — ${effectiveProgramName}` : ''}
                </Text>
                <Text style={styles.paragraph}>{frameworkText(effectiveProgramPurpose)}</Text>
              </View>
            ) : null}
            {programOAPs.length > 0 && (
              <GluedSection
                heading={
                  <>
                    {!frameworkText(effectiveProgramPurpose) && (
                      <Text style={styles.h1}>
                        {labels.program}
                        {effectiveProgramName ? ` — ${effectiveProgramName}` : ''}
                      </Text>
                    )}
                    <Text style={styles.h2}>{labels.programObjectives}</Text>
                  </>
                }
                rows={programOAPs.map((o) => (
                  <View key={o.id} style={styles.bullet} wrap={false}>
                    <Text style={styles.bulletDot}>•</Text>
                    <Text style={styles.bulletText}>
                      {o.code ? `${o.code}. ` : ''}
                      {o.text}
                    </Text>
                  </View>
                ))}
              />
            )}
          </View>
        )}

        {/* Curso: descripción + OAC juntos */}
        <GluedSection
          heading={<Text style={styles.h1}>{labels.courseDescription}</Text>}
          rows={docBlocks(description)}
        />
        {courseOACs.length > 0 && (
          <GluedSection
            heading={<Text style={styles.h2}>{labels.courseObjectives}</Text>}
            rows={courseOACs.map((o) => (
              <View key={o.id} style={styles.bullet} wrap={false}>
                <Text style={styles.bulletDot}>•</Text>
                <Text style={styles.bulletText}>
                  {o.code ? `${o.code}. ` : ''}
                  {o.text}
                </Text>
              </View>
            ))}
          />
        )}

        {/* S4: «Filosofía del curso» quedaba sola al pie: el título iba en un
            <View> que se parte. Ahora viaja pegado a su primer compromiso. */}
        <GluedSection
          heading={<Text style={styles.h1}>{labels.philosophy}</Text>}
          rows={philosophy.map((c) => (
            <View key={c.id} wrap={false} style={{ marginBottom: 6 }}>
              <Text style={styles.h3}>{c.title}</Text>
              <Text style={styles.paragraph}>{c.description}</Text>
            </View>
          ))}
        />

        <GluedSection
          heading={<Text style={styles.h1}>{labels.requiredTexts}</Text>}
          rows={template.readings.required.map((r, i) => (
            <View key={i} style={styles.bullet} wrap={false}>
              <Text style={styles.bulletDot}>•</Text>
              <Text style={styles.bulletText}>
                {formatTb(r.textbookId, r.notes ? { notes: r.notes } : undefined)}
              </Text>
            </View>
          ))}
        />

        {(() => {
          const renderDay = (
            w: SyllabusPdfData['template']['sessionFormat']['weekdayTemplates'][number],
            prefix?: ReactNode,
          ) => {
            const timeline = buildDayTimeline(w.startTime, w.segments, labels);
            return (
              <View key={w.id} style={{ marginBottom: 10 }}>
                <GluedSection
                  heading={
                    <>
                      {prefix ?? null}
                      <Text style={styles.h2}>
                        {capitalize(labels.weekday(w.weekday))} ({w.startTime} – {w.endTime})
                      </Text>
                    </>
                  }
                  {...(timeline.length === 0
                    ? {}
                    : {
                        header: (
                          <View style={styles.tableHeader}>
                            <Text style={styles.cellWide}>{labels.colBlock}</Text>
                            <Text style={styles.cellWide}>{labels.colSchedule}</Text>
                          </View>
                        ),
                      })}
                  rows={
                    timeline.length === 0
                      ? [
                          <Text key="invalid" style={styles.paragraph}>
                            {labels.invalidSchedule}
                          </Text>,
                        ]
                      : timeline.map((row, i) => (
                          <View key={i} style={styles.tableRow} wrap={false}>
                            <Text style={styles.cellWide}>{row.label}</Text>
                            <Text style={styles.cellWide}>
                              {row.range}
                              {row.durationLabel ? ` (${row.durationLabel})` : ''}
                            </Text>
                          </View>
                        ))
                  }
                />
              </View>
            );
          };
          const [firstDay, ...restDays] = template.sessionFormat.weekdayTemplates;
          return (
            <>
              {/* Sin jornadas no hay sección (S4: una sección vacía no se imprime). */}
              {firstDay
                ? renderDay(
                    firstDay,
                    <>
                      <Text style={styles.h1}>{labels.sessionFormat}</Text>
                      <Text style={styles.paragraph}>
                        {template.sessionFormat.introNote || labels.defaultSessionFormatNote}
                      </Text>
                    </>,
                  )
                : null}
              {restDays.map((d) => renderDay(d))}
            </>
          );
        })()}

        {(() => {
          const renderModule = (
            m: SyllabusPdfData['template']['modules'][number],
            prefix?: ReactNode,
          ) => {
            const sched = moduleSchedule.find((s) => s.moduleId === m.id);
            const sessions = sched?.sessions ?? [];
            // «Textos clave» solo si alguna sesión del módulo tiene (S2).
            const keyTexts = showKeyTextsColumn(sessions);
            return (
              <View key={m.id} style={{ marginBottom: 6 }}>
                {/* Encabezado + cabecera de tabla + primera fila en un bloque que
                    no se parte: es chico por construcción. Las filas restantes
                    paginan solas, para que una tabla larga pueda cruzar páginas. */}
                <View wrap={false}>
                  {prefix ?? null}
                  <Text style={styles.h2}>
                    {m.axisTopic ? `${m.name} — ${m.axisTopic}` : m.name}
                  </Text>
                  {sessions.length === 0 ? (
                    <Text style={styles.paragraph}>{labels.noSessions}</Text>
                  ) : (
                    <>
                      <View style={styles.tableHeader}>
                        <Text style={styles.cellTight}>{labels.colDate}</Text>
                        <Text style={styles.cellTight}>{labels.colSchedule}</Text>
                        <Text style={styles.cellWide}>{labels.colTopic}</Text>
                        {keyTexts && <Text style={styles.cell}>{labels.colKeyTexts}</Text>}
                      </View>
                      <View style={styles.tableRow}>
                        <Text style={styles.cellTight}>{sessions[0]!.date}</Text>
                        <Text style={styles.cellTight}>
                          {sessions[0]!.startTime}–{sessions[0]!.endTime}
                        </Text>
                        <Text style={styles.cellWide}>{sessions[0]!.topic}</Text>
                        {keyTexts && (
                          <Text style={styles.cell}>{sessions[0]!.scriptureRefs || '—'}</Text>
                        )}
                      </View>
                    </>
                  )}
                </View>
                {sessions.slice(1).map((s, i) => (
                  <View key={i + 1} style={styles.tableRow} wrap={false}>
                    <Text style={styles.cellTight}>{s.date}</Text>
                    <Text style={styles.cellTight}>
                      {s.startTime}–{s.endTime}
                    </Text>
                    <Text style={styles.cellWide}>{s.topic}</Text>
                    {keyTexts && <Text style={styles.cell}>{s.scriptureRefs || '—'}</Text>}
                  </View>
                ))}
              </View>
            );
          };
          const [first, ...rest] = template.modules;
          return (
            <>
              {first
                ? renderModule(first, <Text style={styles.h1}>{labels.scheduleByModule}</Text>)
                : null}
              {rest.map((m) => renderModule(m))}
            </>
          );
        })()}

        {/* "Detalles de la tarea": cada evaluación descrita una vez, en el mismo
            orden de la tabla de porcentajes. La prosa libre de `assignmentsNote`
            NO se suprime cuando además hay descripciones: los párrafos de un
            autor no los escondemos nosotros. Una plantilla que trae las dos cosas
            imprime las dos.
            Sólo el encabezado queda pegado a sus primeras líneas (`wrap` en el
            bloque, `minPresenceAhead`): una descripción más larga que una página
            debe continuar en la siguiente, no recortarse. */}
        {/* A section can EMPTY a text on purpose; an empty doc is absent here. */}
        {(hasRichText(template.assignmentsNote) ||
          template.grading.components.some((c) => hasRichText(c.description))) && (
          <GluedSection
            heading={<Text style={styles.h1}>{labels.assignmentDetails}</Text>}
            {...(hasRichText(template.assignmentsNote)
              ? { header: <RenderDoc doc={template.assignmentsNote!} /> }
              : {})}
            rows={template.grading.components
              .filter((c) => hasRichText(c.description))
              .map((c) => (
                // El nombre de la evaluación se pega a las primeras líneas de su
                // descripción; el resto del texto pagina solo, porque una
                // descripción larga no cabe en una página y forzarla la
                // superpondría.
                <View key={c.id}>
                  {(() => {
                    const [firstBlock, ...restBlocks] = docBlocks(c.description!);
                    return (
                      <>
                        <View wrap={false}>
                          <Text style={styles.h2}>{c.name}</Text>
                          {firstBlock ?? null}
                        </View>
                        {restBlocks}
                      </>
                    );
                  })()}
                </View>
              ))}
          />
        )}

        {/* "Distribución de la tarea": qué trabajo sirve a qué objetivo del curso. Columns are the EFFECTIVE objectives — the same ones the
            document prints above — so a reader can match them by eye. */}
        {/* Printed only when at least one cell is marked. A grid full of blanks is
            not an empty section: it ASSERTS that no work serves any objective,
            which is what a template whose tags no longer resolve would say. */}
        {distribution.some((r) => r.marks.some(Boolean)) && courseOACs.length > 0 && (
          <GluedSection
            heading={<Text style={styles.h1}>{labels.distribution}</Text>}
            header={
              <View style={styles.rubricHeader}>
                <Text style={[styles.rubricCellCriterion, styles.rubricHeaderText]}> </Text>
                {courseOACs.map((o, i) => (
                  <Text key={o.id} style={[styles.distCell, styles.rubricHeaderText]}>
                    {/* truthiness, not `??`: the PDF importer stores `code: ''`, and a
                      nullish check would leave the column header blank. */}
                    {o.code || labels.objectiveShort(i + 1)}
                  </Text>
                ))}
              </View>
            }
            rows={distribution.map((row) => (
              <View key={row.componentId} style={styles.rubricRow} wrap={false}>
                <Text style={[styles.rubricCellCriterion, styles.rubricCellText]}>{row.title}</Text>
                {row.marks.map((marked, i) => (
                  <Text key={i} style={[styles.distCell, styles.rubricCellText]}>
                    {marked ? 'X' : ''}
                  </Text>
                ))}
              </View>
            ))}
          />
        )}

        {/* La nota de evaluación, cuando existe, va debajo del encabezado de la
            sección y arriba de la tabla. Cuando NO existe, el encabezado se
            quedaría solo colgando de la tabla, así que viaja pegado a ella. */}
        {template.grading.note && (
          <GluedSection
            heading={<Text style={styles.h1}>{labels.assessment}</Text>}
            rows={docBlocks(template.grading.note)}
          />
        )}

        <GluedSection
          heading={
            <>
              {!template.grading.note && <Text style={styles.h1}>{labels.assessment}</Text>}
              <Text style={styles.h2}>{labels.grades}</Text>
            </>
          }
          header={
            <View style={styles.tableHeader}>
              <Text style={styles.cellWide}>{labels.colComponent}</Text>
              <Text style={styles.cellTight}>{labels.colWeight}</Text>
            </View>
          }
          rows={[
            ...template.grading.components.map((c) => (
              <View key={c.id} style={styles.tableRow} wrap={false}>
                <Text style={styles.cellWide}>{c.name}</Text>
                <Text style={styles.cellTight}>{c.weight} %</Text>
              </View>
            )),
            <Text key="passing" style={styles.paragraph}>
              {labels.passingGrade(template.grading.passingGrade)}
            </Text>,
          ]}
        />

        {/* "Volumen de la tarea en horas" — a TABLE with a TOTAL row, the way the
            reference document prints it. It used to be flattened into one run-on
            sentence, which lost the one number a student actually looks for. */}
        {workload.rows.length > 0 && (
          <GluedSection
            heading={<Text style={styles.h2}>{labels.workload}</Text>}
            header={
              <View style={styles.tableHeader}>
                <Text style={styles.cellWide}>{labels.colTask}</Text>
                <Text style={styles.cellTight}>{labels.colHours}</Text>
              </View>
            }
            rows={[
              ...workload.rows.map((r) => (
                <View key={`${r.graded ? 'c' : 'h'}-${r.id}`} style={styles.tableRow} wrap={false}>
                  <Text style={styles.cellWide}>{r.label}</Text>
                  <Text style={styles.cellTight}>{r.hours}</Text>
                </View>
              )),
              <View key="total" style={styles.tableRow} wrap={false}>
                <Text style={[styles.cellWide, styles.bold]}>{labels.total}</Text>
                <Text style={[styles.cellTight, styles.bold]}>{workload.total}</Text>
              </View>,
            ]}
          />
        )}

        {/* "Calendario del curso": una fila por semana con qué leer y qué
            entregar. Sección propia y con su encabezado — antes colgaba del mismo
            h1 que los detalles y la matriz, de modo que el documento impreso no
            tenía las secciones que el plan de estudio real tiene. Las fechas de
            entrega no se pierden: viajan junto a cada entregable. */}
        <GluedSection
          heading={<Text style={styles.h1}>{labels.courseCalendar}</Text>}
          header={
            <View style={styles.tableHeader}>
              <Text style={styles.cellTight}>{labels.colWeek}</Text>
              <Text style={styles.cellWide}>{labels.colCourseWork}</Text>
            </View>
          }
          // Las filas NO llevan `wrap={false}`: una semana con mucha lectura mide
          // varias líneas y tiene que poder continuar en la página siguiente en
          // vez de recortarse.
          rows={calendarWeeks.map((w, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.cellTight}>{w.weekOffset === null ? '—' : w.weekOffset + 1}</Text>
              <Text style={styles.cellWide}>{calendarLines(w)}</Text>
            </View>
          ))}
        />

        {/* La tabla suelta de lecturas SÓLO cuando ninguna fila tiene semana: si
            la tienen, el "Calendario del curso" ya las imprime semana a semana y
            esta tabla sería la misma lista dos veces en el mismo documento. Una
            plantilla a medio cargar no pierde nada — sus filas sin semana siguen
            saliendo acá. */}
        {template.readings.plan &&
          (!calendarShowsReadings ||
            !template.readings.plan.rows.some((r) => r.weekOffset !== undefined)) &&
          (template.readings.plan.rows.length > 0 || template.readings.plan.description) && (
            <GluedSection
              heading={
                <Text style={styles.h1}>
                  {template.readings.plan.introNote ||
                    labels.readingsPlanHeading(
                      template.readings.plan.rows.reduce((a, r) => a + (Number(r.pages) || 0), 0),
                    )}
                </Text>
              }
              header={
                <>
                  {template.readings.plan.description && (
                    <Text style={styles.paragraph}>{template.readings.plan.description}</Text>
                  )}
                  {template.readings.plan.rows.length > 0 && (
                    <View style={styles.tableHeader}>
                      <Text style={styles.cellWide}>{labels.colReading}</Text>
                      <Text style={styles.cellTight}>{labels.colPages}</Text>
                    </View>
                  )}
                </>
              }
              rows={[
                ...template.readings.plan.rows.map((row) => (
                  <View key={row.id} style={styles.tableRow}>
                    <Text style={styles.cellWide}>{row.label}</Text>
                    <Text style={styles.cellTight}>{row.pages}</Text>
                  </View>
                )),
                ...(template.readings.plan.closingNote
                  ? [
                      <Text key="closing" style={[styles.paragraph, { marginTop: 6 }]}>
                        {template.readings.plan.closingNote}
                      </Text>,
                    ]
                  : []),
              ]}
            />
          )}

        {data.referencedRubrics && data.referencedRubrics.length > 0 && (
          <View>
            {data.referencedRubrics.map((rubric, rubricIdx) => {
              const usesLevels = rubric.criteria.some((c) => c.levels && c.levels.length > 0);
              // Use the FIRST criterion's level names as the canonical column
              // headers — assume all criteria in a rubric share the same level
              // set (standard rubric design). The renderer is tolerant: missing
              // cells render as "—".
              const levelNames = usesLevels
                ? (rubric.criteria[0]?.levels?.map((l) => l.name) ?? [])
                : [];
              // Render one criterion row. Hoisted so the first row can travel
              // with the title + header (orphan guard) while the rest flow.
              const renderCriterionRow = (c: (typeof rubric.criteria)[number]) => (
                <View key={c.id} wrap={false} style={styles.rubricRow}>
                  <View style={styles.rubricCellCriterion}>
                    <Text style={styles.rubricCriterionLabel}>{c.label}</Text>
                    {c.description ? (
                      <Text style={styles.rubricCriterionDesc}>{c.description}</Text>
                    ) : null}
                  </View>
                  {usesLevels &&
                    levelNames.map((name) => {
                      const lvl = (c.levels ?? []).find((x) => x.name === name);
                      return (
                        <View key={name} style={styles.rubricCellLevel}>
                          <Text style={styles.rubricCellText}>{lvl?.description ?? '—'}</Text>
                        </View>
                      );
                    })}
                  <View style={styles.rubricCellWeight}>
                    <Text style={styles.rubricCellText}>{c.weight} %</Text>
                  </View>
                </View>
              );
              return (
                // The first rubric always starts on a fresh page via
                // `break`. react-pdf's content-aware break controls
                // (`wrap={false}`, `minPresenceAhead`) proved unreliable on
                // this nested table — they either orphaned the section
                // heading at the bottom of the previous page or rendered
                // rows on top of each other. Forcing the section to a new
                // page is deterministic at the cost of some empty space on
                // the previous page. Subsequent rubrics flow normally.
                <View key={rubric.id} style={{ marginBottom: 14 }} break={rubricIdx === 0}>
                  {/* S4: el título de cada rúbrica viaja con su cabecera y su
                      primer criterio; el resto de las filas pagina solo. */}
                  <View wrap={false}>
                    {rubricIdx === 0 && <Text style={styles.h1}>{labels.rubrics}</Text>}
                    <Text style={styles.h2}>{rubric.title}</Text>
                    {usesLevels ? (
                      <View style={styles.rubricHeader}>
                        <View style={styles.rubricCellCriterion}>
                          <Text style={styles.rubricHeaderText}>{labels.colCriterion}</Text>
                        </View>
                        {levelNames.map((name) => (
                          <View key={name} style={styles.rubricCellLevel}>
                            <Text style={styles.rubricHeaderText}>{name}</Text>
                          </View>
                        ))}
                        <View style={styles.rubricCellWeight}>
                          <Text style={styles.rubricHeaderText}>{labels.colWeight}</Text>
                        </View>
                      </View>
                    ) : (
                      <View style={styles.rubricHeader}>
                        <View style={styles.rubricCellCriterion}>
                          <Text style={styles.rubricHeaderText}>{labels.colCriterion}</Text>
                        </View>
                        <View style={styles.rubricCellWeight}>
                          <Text style={styles.rubricHeaderText}>{labels.colWeight}</Text>
                        </View>
                      </View>
                    )}
                    {rubric.criteria[0] ? renderCriterionRow(rubric.criteria[0]) : null}
                  </View>
                  {rubric.criteria.slice(1).map(renderCriterionRow)}
                </View>
              );
            })}
          </View>
        )}

        {(template.policiesNote || template.latePolicy) && (
          <View>
            {/* S4: cada título pegado a su párrafo. */}
            <View wrap={false}>
              <Text style={styles.h1}>{labels.policies}</Text>
              <Text style={styles.paragraph}>
                {template.policiesNote || labels.defaultPoliciesNote}
              </Text>
              {!template.latePolicy && anchorWithPolicies}
            </View>
            {template.latePolicy && (
              <View wrap={false}>
                <Text style={styles.h2}>{labels.latePolicyHeading}</Text>
                <Text style={styles.paragraph}>
                  {labels.latePolicyParagraph(template.latePolicy)}
                </Text>
                {anchorWithPolicies}
              </View>
            )}
          </View>
        )}

        {/* S4: sin ítems, la bibliografía complementaria no se imprime (la
            introducción sola presenta una lista que no está). El ancla bíblica
            viaja pegada al último ítem, para no terminar sola en una página. */}
        {hasComplementary && (
          <GluedSection
            heading={<Text style={styles.h1}>{labels.complementaryBibliography}</Text>}
            {...(anchorBlock ? { tail: anchorBlock } : {})}
            {...(template.readings.complementary.intro
              ? {
                  header: (
                    <Text style={[styles.paragraph, { fontStyle: 'italic' }]}>
                      {template.readings.complementary.intro}
                    </Text>
                  ),
                }
              : {})}
            rows={template.readings.complementary.items.map((r, i) => (
              <View key={i} style={styles.bullet} wrap={false}>
                <Text style={styles.bulletDot}>•</Text>
                <Text style={styles.bulletText}>
                  {formatTb(r.textbookId, r.notes ? { notes: r.notes } : undefined)}
                </Text>
              </View>
            ))}
          />
        )}

        {/* Sin bibliografía complementaria ni políticas, el ancla va sola. */}
        {anchorBlock && !hasComplementary && !hasPolicies && (
          <View wrap={false}>{anchorBlock}</View>
        )}
        <Text style={styles.footer} render={() => headerLabel} fixed />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
